/**
 * Concrete CustomerRepository talking to /api/v1/admin/customers/.
 * (Infrastructure layer — only place that knows axios, URLs, etc.)
 */
import type { AxiosInstance } from 'axios';

import type { CustomerDetail, CustomerListRow } from '@domain/customer/Customer';
import type {
  BalanceAdjustment,
  CustomerEditableFields,
  CustomerRepository,
  ListPage,
  ListParams,
} from '@domain/customer/CustomerRepository';

import { ADMIN_API_BASE } from './client';

export class HttpCustomerRepository implements CustomerRepository {
  constructor(private readonly api: AxiosInstance) {}

  async list(params: ListParams): Promise<ListPage<CustomerListRow>> {
    const query: Record<string, string | number> = {
      limit: params.limit,
    };
    if (params.offset !== undefined) query.offset = params.offset;
    if (params.cursor) query.offset = Number(params.cursor); // current API is offset-only
    if (params.sort) {
      query.sort = (params.sort.desc ? '-' : '') + params.sort.id;
    }
    if (params.filters) {
      for (const [k, v] of Object.entries(params.filters)) {
        query[k] = String(v);
      }
    }
    const res = await this.api.get(`${ADMIN_API_BASE}/customers/`, { params: query });
    return res.data as ListPage<CustomerListRow>;
  }

  async get(id: string): Promise<CustomerDetail> {
    const res = await this.api.get(`${ADMIN_API_BASE}/customers/${id}/`);
    return res.data as CustomerDetail;
  }

  async suspend(id: string, reason?: string): Promise<CustomerDetail> {
    const res = await this.api.post(
      `${ADMIN_API_BASE}/customers/${id}/suspend/`,
      reason ? { reason } : {},
    );
    return res.data as CustomerDetail;
  }

  async activate(id: string, reason?: string): Promise<CustomerDetail> {
    const res = await this.api.post(
      `${ADMIN_API_BASE}/customers/${id}/activate/`,
      reason ? { reason } : {},
    );
    return res.data as CustomerDetail;
  }

  async adjustBalance(id: string, adjustment: BalanceAdjustment): Promise<CustomerDetail> {
    const res = await this.api.post(
      `${ADMIN_API_BASE}/customers/${id}/adjust-balance/`,
      adjustment,
    );
    return res.data as CustomerDetail;
  }

  async patch(id: string, fields: CustomerEditableFields): Promise<CustomerDetail> {
    const res = await this.api.patch(`${ADMIN_API_BASE}/customers/${id}/`, fields);
    return res.data as CustomerDetail;
  }

  async changePlan(id: string, planId: string, reason: string): Promise<CustomerDetail> {
    const res = await this.api.post(
      `${ADMIN_API_BASE}/customers/${id}/change-plan/`,
      { plan_id: planId, reason },
    );
    return res.data as CustomerDetail;
  }
}
